import { useSyncExternalStore } from 'react';
import { config } from '../config';

// Sign-in through /auth on this origin (doorman's cookie mode). The session
// lives in cookies the browser sends by itself: an HttpOnly access token on
// every request, an HttpOnly refresh token only to /auth/refresh, and
// doorman_csrf, which this page reads and echoes as X-CSRF-Token on every
// change. No token is ever in JavaScript. Each refresh token works once, and
// showing one that was already used ends the session, and every tab shares
// the cookie, so refreshes run one at a time across tabs.

const CSRF_COOKIE = 'doorman_csrf';
const CSRF_HEADER = 'X-CSRF-Token';
/** Tells other tabs that this one signed in or out: cookies have no change event everywhere. */
const MARK = 'wellread-signed-in';
/** Where the token sessions were kept before; they mean nothing now. */
const LEGACY_KEYS = ['wellread-session', 'wellread-auth'];
const LOCK = 'wellread-refresh';

export type AuthState = { status: 'signed-out' } | { status: 'signed-in' };

/** A sign-in that didn't go through, worded for the reader. */
export class AuthError extends Error {
  constructor(
    readonly code: string,
    message: string,
    readonly status: number,
  ) {
    super(message);
    this.name = 'AuthError';
  }
}

/** The session's CSRF token, which is also the sign that there is a session. */
export function csrfToken(): string | null {
  if (typeof document === 'undefined') return null;
  for (const part of document.cookie.split(';')) {
    const [name, ...value] = part.trim().split('=');
    if (name === CSRF_COOKIE) return decodeURIComponent(value.join('=')) || null;
  }
  return null;
}

/** Headers for a change: the CSRF token, when there is a session. */
export function csrfHeaders(): Record<string, string> {
  const t = csrfToken();
  return t ? { [CSRF_HEADER]: t } : {};
}

let state: AuthState = { status: 'signed-out' };
const listeners = new Set<() => void>();

/** Reads the session from the cookie, telling the screens only when it changes. */
function sync(): AuthState {
  const status = csrfToken() ? 'signed-in' : 'signed-out';
  if (status !== state.status) {
    state = { status };
    for (const l of listeners) l();
  }
  return state;
}

/** Records a sign-in or sign-out here and tells the other tabs. */
function announce() {
  sync();
  try {
    localStorage.setItem(MARK, `${state.status} ${Date.now()}`);
  } catch {
    // Storage blocked: other tabs find out on their next request.
  }
}

/** Forgets the session here: the CSRF cookie is the only one this page can clear. */
function forget() {
  document.cookie = `${CSRF_COOKIE}=; Max-Age=0; Path=/`;
  announce();
}

try {
  for (const k of LEGACY_KEYS) localStorage.removeItem(k);
} catch {
  // nothing stored, or storage blocked
}
sync();
if (typeof window !== 'undefined') {
  // Another tab signed in or out.
  window.addEventListener('storage', (e) => {
    if (e.key === MARK || e.key === null) sync();
  });
  // Coming back to the tab: the session may have ended or begun elsewhere.
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') sync();
  });
}

export function useAuth(): AuthState {
  return useSyncExternalStore(
    (cb) => {
      listeners.add(cb);
      return () => listeners.delete(cb);
    },
    () => state,
  );
}

/** Whether someone is signed in on this device. The cookie is right here, so this never waits. */
export function authReady(): Promise<AuthState> {
  return Promise.resolve(sync());
}

// ---- the /auth endpoints ------------------------------------------------------

function wait(retryAfter: string | null): string {
  const s = Number(retryAfter);
  if (!retryAfter || !Number.isFinite(s) || s <= 0) return 'a minute';
  if (s <= 90) return `${Math.ceil(s)} seconds`;
  const m = Math.ceil(s / 60);
  return m === 1 ? 'a minute' : `${m} minutes`;
}

/** The server's error code, said from the reader's side. */
export function phrase(code: string, retryAfter: string | null = null): string {
  switch (code) {
    case 'invalid_code':
      return 'That code has expired or doesn’t match. Send a new one.';
    case 'too_many_attempts':
      return 'Too many wrong codes. Send a new one.';
    case 'rate_limited':
      return `Too many tries. Wait ${wait(retryAfter)}, then try again.`;
    case 'invalid_input':
      return 'Check the email address and try again.';
    case 'user_disabled':
      return 'This account can’t sign in.';
    case 'invalid_token':
    case 'invalid_refresh_token':
    case 'refresh_token_reused':
      return 'Your session has ended. Sign in again.';
    case 'provider_not_configured':
      return 'Signing in by email isn’t available right now.';
    case 'offline':
      return 'No connection. Check you’re online and try again.';
    default:
      return 'Signing in stopped. Try again.';
  }
}

/** Calls /auth: same origin, so the cookies go along; every change carries the CSRF token. */
async function send(
  method: string,
  path: string,
  body?: unknown,
  extra: Record<string, string> = {},
): Promise<void> {
  let res: Response;
  try {
    res = await fetch(`${config.apiUrl}/auth${path}`, {
      method,
      credentials: 'same-origin',
      headers: {
        ...(body === undefined ? {} : { 'Content-Type': 'application/json' }),
        ...csrfHeaders(),
        ...extra,
      },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch {
    throw new AuthError('offline', phrase('offline'), 0);
  }
  if (res.ok) return;
  const data = (await res.json().catch(() => ({}))) as { error?: string };
  const code = data.error ?? 'internal';
  throw new AuthError(code, phrase(code, res.headers.get('Retry-After')), res.status);
}

// ---- signing in and out -------------------------------------------------------

/** Emails a 6-digit code to the address. Signing up and logging in are the same. */
export async function sendCode(email: string): Promise<void> {
  await send('POST', '/otp/start', { kind: 'email', target: email.trim() });
}

/** Exchanges the emailed code for a session on this device, kept in cookies. */
export async function verifyCode(email: string, code: string): Promise<void> {
  await send(
    'POST',
    '/otp/verify',
    { kind: 'email', target: email.trim(), code: code.trim() },
    { 'X-Doorman-Transport': 'cookie' },
  );
  announce();
}

/**
 * Signs out on this device. Unless local, the server ends the session too
 * when it can be reached (and clears its cookies); it's over here either way.
 */
export async function signOut({ local = false }: { local?: boolean } = {}): Promise<void> {
  if (!local && csrfToken()) await send('POST', '/signout').catch(() => undefined);
  forget();
}

/**
 * Closes the account for good: the server deletes the books, notes and
 * files, then the sign-in itself, and clears the session cookies.
 */
export async function deleteAccount(): Promise<void> {
  try {
    await send('DELETE', '/me');
  } catch (e) {
    if (e instanceof AuthError && e.code !== 'offline') {
      throw new AuthError(e.code, 'Your account couldn’t be deleted. Try again.', e.status);
    }
    throw e;
  }
  forget();
}

// ---- Google and Apple ---------------------------------------------------------

// The browser leaves for the provider and comes back through this origin's
// /auth callback, which sets the session cookies (or adds ?error=) and sends
// it on to /sign-in/done.

export type Provider = 'google' | 'apple';

const NAMES: Record<Provider, string> = { google: 'Google', apple: 'Apple' };

/** Where the provider's sign-in starts; it returns to /sign-in/done, then next. */
export function providerStartUrl(provider: Provider, next: string): string {
  const back = `/sign-in/done?via=${provider}&next=${encodeURIComponent(next)}`;
  return `${config.apiUrl}/auth/${provider}/start?redirect=${encodeURIComponent(back)}`;
}

/** Leaves for Google or Apple. */
export function startProvider(
  provider: Provider,
  next: string,
  go: (url: string) => void = (url) => window.location.assign(url),
): void {
  go(providerStartUrl(provider, next));
}

/** A Google or Apple sign-in that didn't go through, said from the reader's side. */
export function providerPhrase(code: string, provider: Provider | null): string {
  const name = provider ? NAMES[provider] : null;
  switch (code) {
    case 'cancelled':
      return name ? `Sign-in with ${name} was cancelled.` : 'Sign-in was cancelled.';
    case 'invalid_token':
      return 'That sign-in took too long. Try again.';
    case 'identity_in_use':
      return name
        ? `That ${name} account belongs to another reader.`
        : 'That account belongs to another reader.';
    case 'user_disabled':
      return 'This account can’t sign in.';
    case 'provider_error':
      return `${name ?? 'The sign-in provider'} didn’t finish the sign-in. Try again.`;
    case 'provider_not_configured':
      return `Signing in with ${name ?? 'that'} isn’t available right now.`;
    case 'offline':
      return phrase('offline');
    default:
      return 'Signing in stopped. Try again.';
  }
}

/** A Google or Apple sign-in that came back without a session: which one, and where it was going. */
export class ProviderError extends AuthError {
  constructor(
    code: string,
    readonly provider: Provider | null,
    readonly next: string,
  ) {
    super(code, providerPhrase(code, provider), 0);
    this.name = 'ProviderError';
  }
}

/**
 * Where Google or Apple came back: the callback already set the session
 * cookies, or says in ?error= why it didn't. Returns where to go next.
 */
export function finishProvider(params: URLSearchParams): string {
  const via = params.get('via');
  const provider = via === 'google' || via === 'apple' ? via : null;
  const next = params.get('next') ?? '/';
  const error = params.get('error');
  if (error) throw new ProviderError(error, provider, next);
  if (!csrfToken()) throw new ProviderError('invalid_token', provider, next);
  announce();
  return next;
}

// ---- keeping the session ------------------------------------------------------

let refreshing: Promise<boolean> | null = null;
let unreachable = false;

/** Runs fn while no other tab is refreshing, where the browser can say so. */
function exclusive<T>(fn: () => Promise<T>): Promise<T> {
  const locks = typeof navigator === 'undefined' ? undefined : navigator.locks;
  return locks ? (locks.request(LOCK, fn) as Promise<T>) : fn();
}

/**
 * Renews the session after the API turned down a request sent with the CSRF
 * token seen: the access cookie expired. True if the request may be sent
 * again. If the token has changed since, another tab (or request) already
 * renewed it, so this doesn't. A 401 or 403 from /auth/refresh ends the
 * session (the server clears its cookies); no connection or a server error
 * keeps it, and marks the server unreachable.
 */
export function refreshSession(seen: string | null): Promise<boolean> {
  refreshing ??= exclusive(async () => {
    const now = csrfToken();
    if (!now) {
      sync();
      return false;
    }
    if (seen !== null && now !== seen) return true;
    try {
      await send('POST', '/refresh');
      unreachable = false;
      return true;
    } catch (e) {
      if (e instanceof AuthError && (e.status === 401 || e.status === 403)) {
        unreachable = false;
        forget();
      } else {
        unreachable = true;
      }
      return false;
    }
  }).finally(() => (refreshing = null));
  return refreshing;
}

/** What the API client needs: the token to send on changes, and a refresh after a 401. */
export const session = { csrf: csrfToken, refresh: refreshSession };

/** Whether the last refresh couldn't reach the server, so a 401 since says nothing about the session. */
export const authUnreachable = () => unreachable;
